// ControlsCoverageFindingsPanel — Phase 4 G3.7 UI (2026-05-29).
//
// Surfaces PEG-CONTROLS-COVERAGE-COMPLETENESS-001 resolver findings
// on the G3.7 review surface. The compliance agent's output is
// read-only here — the architect fixes findings either by editing
// phase3/compliance-risks.md directly (add a hazard row citing the
// missing clause + naming a mitigation) OR by adding a waiver to
// phase4/audit-diff.waivers.json.
//
// Layout (mirrors ThreatModelFindingsPanel):
//
//   ┌──────────────────────────────────────────────────────────────┐
//   │ Header: regulatory · shape · N findings across M controls    │
//   ├──────────────────────────────────────────────────────────────┤
//   │ Structural-defect group (red border, expanded) when the      │
//   │ no_hazard_tables finding fires.                              │
//   │   ⌄ (no hazard tables) · 1 finding                           │
//   ├──────────────────────────────────────────────────────────────┤
//   │ Per-clause accordion. Clauses with findings expanded by      │
//   │ default; cited clauses collapsed with a "cited" chip.        │
//   │   ⌄ PCI DSS v4.0.1 Req. 6 · not cited                        │
//   │     • [high] missing_in_scope_control                        │
//   │       Suggested: Add a hazard-record entry citing Req. 6…    │
//   │   ⌃ ✓ PCI DSS v4.0.1 Req. 3 · cited                          │
//   ├──────────────────────────────────────────────────────────────┤
//   │ Help footer: how to remediate / waive                        │
//   └──────────────────────────────────────────────────────────────┘

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
} from 'lucide-react'

import { FINDING_PRESENTATION } from '@/lib/findingPresentation'
import {
  getControlsCoverageFindings,
  regenerateControlsCoverage,
} from '@/lib/controlsCoverageFindingsApi'
import {
  CC_INVARIANT_ID,
  CC_KIND_LABEL,
  CC_SEVERITY_PALETTE,
  type ControlsCoverageFindingRow,
  type ControlsCoverageFindingsResponse,
  type InScopeControlRow,
} from '@/types/controlsCoverageFindings'
import { WaivedFindingsDisclosure } from './WaivedFindingsDisclosure'
import { WaiveFindingModal } from './WaiveFindingModal'

export interface ControlsCoverageFindingsPanelProps {
  projectId: string
  runId: number
}

interface ClauseGroup {
  /** ``null`` is the synthetic group for structural-defect findings
   *  (``no_hazard_tables``) that don't belong to any specific clause. */
  clause: string | null
  control: InScopeControlRow | null
  findings: ControlsCoverageFindingRow[]
}

function groupByClause(
  rows: ControlsCoverageFindingRow[],
  inventory: InScopeControlRow[],
): ClauseGroup[] {
  // Synthetic group for structural-defect findings (no_hazard_tables).
  const synthetic: ControlsCoverageFindingRow[] = rows.filter(
    (r) => r.finding_kind === 'no_hazard_tables',
  )
  // Per-clause buckets from inventory order (so the layout is
  // stable + predictable).
  const byClause = new Map<string, ControlsCoverageFindingRow[]>()
  for (const ctrl of inventory) byClause.set(ctrl.clause, [])
  for (const f of rows) {
    if (f.finding_kind === 'no_hazard_tables') continue
    if (!f.clause) continue
    if (!byClause.has(f.clause)) byClause.set(f.clause, [])
    byClause.get(f.clause)!.push(f)
  }
  const groups: ClauseGroup[] = []
  if (synthetic.length > 0) {
    groups.push({ clause: null, control: null, findings: synthetic })
  }
  // Map inventory → group preserving inventory order. Findings count
  // descends (cited first vs not-cited isn't strictly count, but the
  // not-cited groups carry findings and the cited ones don't, so the
  // count split is correct).
  const invByClause = new Map(inventory.map((c) => [c.clause, c]))
  const orderedClauses = inventory.map((c) => c.clause)
  // Stable sort: not-cited first (most pressing), then cited.
  orderedClauses.sort((a, b) => {
    const ca = byClause.get(a)?.length ?? 0
    const cb = byClause.get(b)?.length ?? 0
    if (ca !== cb) return cb - ca
    return 0
  })
  for (const clause of orderedClauses) {
    groups.push({
      clause,
      control: invByClause.get(clause) ?? null,
      findings: byClause.get(clause) ?? [],
    })
  }
  return groups
}

function FindingCard({
  row,
  onWaive,
}: {
  row: ControlsCoverageFindingRow
  onWaive: () => void
}) {
  const sev = CC_SEVERITY_PALETTE[row.severity]
  return (
    <div className="rounded border border-slate-700/50 bg-slate-950/40 p-2.5 text-sm">
      <div className="mb-1 flex flex-wrap items-center gap-1.5">
        <span
          className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10.5px] font-medium ${sev.chipClass}`}
        >
          {sev.label}
        </span>
        <span className="text-[10.5px] uppercase tracking-wide text-slate-500">
          {CC_KIND_LABEL[row.finding_kind]}
        </span>
      </div>
      <div className="text-slate-200">
        {FINDING_PRESENTATION[row.finding_kind].whyItMatters}
      </div>
      {row.suggested_fix && (
        <div className="mt-1.5 rounded border border-emerald-900/30 bg-emerald-950/15 p-1.5 text-xs text-emerald-200/85">
          <strong className="text-emerald-300">Suggested: </strong>
          {row.suggested_fix}
        </div>
      )}
      {/* Pipeline detail demoted to a collapsible disclosure (legibility
          pass) — the raw resolver message, citations, and rule source. */}
      <details className="mt-1.5 text-[10.5px] text-slate-500">
        <summary className="cursor-pointer select-none text-slate-400">
          Developer detail
        </summary>
        <div className="mt-1 text-slate-400">{row.message}</div>
        {row.cites.length > 0 && (
          <div className="mt-1 text-slate-400">
            cite: {row.cites.join(' · ')}
          </div>
        )}
        <div className="mt-1">
          rule source: <span className="font-mono">{row.rule_source}</span>
        </div>
      </details>
      {/* Resolution action — in-UI waive instead of hand-authoring JSON. */}
      <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-800/50 pt-2">
        <button
          type="button"
          onClick={onWaive}
          className="inline-flex items-center gap-1 rounded border border-amber-700/40 bg-amber-950/20 px-2 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-900/30"
        >
          <ShieldOff className="h-3 w-3" /> Waive
        </button>
      </div>
    </div>
  )
}

function ClauseGroupCard({
  group,
  defaultExpanded,
  onWaive,
}: {
  group: ClauseGroup
  defaultExpanded: boolean
  onWaive: (row: ControlsCoverageFindingRow) => void
}) {
  const [open, setOpen] = useState(defaultExpanded)
  const findingsCount = group.findings.length
  const isClean = findingsCount === 0
  const isSynthetic = group.clause === null
  return (
    <div
      className={`rounded-md border bg-slate-900/40 overflow-hidden ${
        isSynthetic
          ? 'border-red-800/50'
          : isClean
            ? 'border-slate-800/50'
            : 'border-amber-800/40'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-900/60"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <span className="font-mono text-sm font-semibold text-slate-100">
          {group.clause ?? '(no hazard tables)'}
        </span>
        {isClean && !isSynthetic ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
            <CheckCircle2 className="h-3 w-3" /> cited
          </span>
        ) : (
          <span
            className={`inline-flex items-center gap-1 text-xs ${
              isSynthetic ? 'text-red-300' : 'text-amber-300'
            }`}
          >
            <AlertTriangle className="h-3 w-3" />
            {isSynthetic
              ? `${findingsCount} ${findingsCount === 1 ? 'finding' : 'findings'}`
              : 'not cited'}
          </span>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1">
          {group.control && !isSynthetic && (
            <div className="mb-2 rounded border border-slate-800/40 bg-slate-950/30 p-2 text-xs text-slate-400">
              <div className="text-[10.5px] uppercase tracking-wide text-slate-500">
                purpose
              </div>
              <div className="mt-0.5 text-slate-300">{group.control.purpose}</div>
              {group.control.cites.length > 0 && (
                <div className="mt-1 text-[10.5px] text-slate-500">
                  catalog cite: {group.control.cites.join(' · ')}
                </div>
              )}
            </div>
          )}
          {findingsCount > 0 && (
            <div className="flex flex-col gap-2">
              {group.findings.map((f, idx) => (
                <FindingCard
                  key={`${f.finding_kind}-${idx}`}
                  row={f}
                  onWaive={() => onWaive(f)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function ControlsCoverageFindingsPanel({
  projectId,
  runId,
}: ControlsCoverageFindingsPanelProps) {
  const queryKey = ['controls-coverage-findings', projectId, runId]
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery<ControlsCoverageFindingsResponse>(
    {
      queryKey,
      queryFn: () => getControlsCoverageFindings(projectId, runId),
    },
  )

  const [waiving, setWaiving] =
    useState<ControlsCoverageFindingRow | null>(null)

  // G3.7 "Regenerate" — re-run the Compliance agent to author hazard
  // records citing every currently-uncited in-scope control, then
  // refresh the panel. One LLM call server-side (~1–2 min).
  const regenerate = useMutation({
    mutationFn: () => regenerateControlsCoverage(projectId, runId, []),
    onSuccess: (fresh) => {
      queryClient.setQueryData(queryKey, fresh)
      queryClient.invalidateQueries({ queryKey })
    },
    // ~1–2 min server-side LLM call: if a proxy/ingress times the
    // request out the work may still have completed, so refetch on
    // error too rather than stranding the user on a stale error.
    onError: () => queryClient.invalidateQueries({ queryKey }),
  })

  const activeFindings = useMemo(
    () => (data?.findings ?? []).filter((f) => !f.waived),
    [data],
  )
  const waivedFindings = useMemo(
    () => (data?.findings ?? []).filter((f) => f.waived),
    [data],
  )

  const groups = useMemo(() => {
    if (!data) return [] as ClauseGroup[]
    return groupByClause(activeFindings, data.in_scope_controls)
  }, [data, activeFindings])

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading controls-coverage findings…
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded border border-red-800 bg-red-950/30 p-3 text-sm text-red-200">
        Failed to load controls-coverage findings:{' '}
        {String((error as Error).message)}
      </div>
    )
  }
  if (!data) return null

  if (!data.compliance_risks_present) {
    return (
      <div className="rounded border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-300">
        <div className="mb-1 flex items-center gap-2 text-slate-200">
          <Info className="h-4 w-4 text-sky-400" />
          Phase 3 hasn't shipped compliance-risks.md yet
        </div>
        <p className="text-xs text-slate-400">
          The compliance agent runs in Phase 3. Once it produces{' '}
          <code className="rounded bg-slate-800 px-1 py-0.5">
            phase3/compliance-risks.md
          </code>
          , this panel will show any in-scope-control citation misses
          the resolver detects.
        </p>
        {data.in_scope_controls.length > 0 && (
          <div className="mt-2 text-xs text-amber-300">
            Heads-up: {data.in_scope_controls.length} in-scope control
            {data.in_scope_controls.length === 1 ? '' : 's'} must be
            cited once the document lands.
          </div>
        )}
      </div>
    )
  }

  const totalFindings = activeFindings.length
  const missingControls = activeFindings.filter(
    (f) => f.finding_kind === 'missing_in_scope_control',
  ).length
  const totalInScope = data.in_scope_controls.length

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-baseline gap-3 text-xs text-slate-400">
        {data.regulatory.length > 0 && (
          <span>regulatory: {data.regulatory.join(', ')}</span>
        )}
        {data.shape && <span>shape: {data.shape}</span>}
        <span>
          <ShieldCheck className="mr-1 inline-block h-3 w-3" />
          {totalInScope} in-scope control{totalInScope === 1 ? '' : 's'}
        </span>
        {missingControls > 0 && (
          <span className="text-amber-300">
            {missingControls} not cited
          </span>
        )}
        {totalFindings > 0 ? (
          <span className="text-amber-300">
            {totalFindings} {totalFindings === 1 ? 'finding' : 'findings'}
          </span>
        ) : (
          <span className="text-emerald-300">
            <CheckCircle2 className="mr-1 inline-block h-3 w-3" />
            clean
          </span>
        )}
      </div>

      {/* Regenerate — one-click fix for uncited in-scope controls. */}
      {missingControls > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded border border-sky-900/50 bg-sky-950/20 p-3">
          <button
            type="button"
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending}
            className="inline-flex items-center gap-1.5 rounded bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {regenerate.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {regenerate.isPending
              ? 'Authoring the missing controls…'
              : `Regenerate ${missingControls} uncited control${
                  missingControls === 1 ? '' : 's'
                }`}
          </button>
          <span className="text-xs text-slate-400">
            {regenerate.isPending
              ? 'Re-running the compliance assistant — this can take a minute or two.'
              : 'Have the compliance assistant write a hazard record (with a mitigation) for each missing control, then re-check.'}
          </span>
          {regenerate.isError && (
            <span className="flex items-center gap-1 text-xs text-red-300">
              <AlertTriangle className="h-3 w-3" />
              Couldn't regenerate: {String(
                (regenerate.error as Error).message,
              )}
            </span>
          )}
        </div>
      )}

      {/* Groups */}
      {groups.length === 0 ? (
        <div className="rounded border border-slate-700 bg-slate-900/40 p-3 text-sm text-slate-400">
          No in-scope controls declared for this project's profiles.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {groups.map((g) => (
            <ClauseGroupCard
              key={g.clause ?? '__synthetic__'}
              group={g}
              defaultExpanded={g.findings.length > 0}
              onWaive={setWaiving}
            />
          ))}
        </div>
      )}

      {/* Waived findings — accepted, collapsed out of the way. */}
      <WaivedFindingsDisclosure
        items={waivedFindings.map((f, idx) => ({
          id: `${f.finding_kind}-${f.clause ?? ''}-${idx}`,
          label: `${CC_KIND_LABEL[f.finding_kind]}${
            f.clause ? ` · ${f.clause}` : ''
          }`,
          justification: f.waiver_justification,
        }))}
      />

      {waiving && (
        <WaiveFindingModal
          projectId={projectId}
          runId={runId}
          invariantId={CC_INVARIANT_ID}
          findingKey={waiving.finding_key}
          findingLabel={`${CC_KIND_LABEL[waiving.finding_kind]}${
            waiving.clause ? ` · ${waiving.clause}` : ''
          }`}
          refetchQueryKey={queryKey}
          onClose={() => setWaiving(null)}
        />
      )}

      {/* Help footer */}
      <div className="rounded border border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-400">
        <div className="mb-1 flex items-center gap-2 text-slate-300">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          How to clear these findings
        </div>
        <p>
          <strong className="text-sky-200">Required control not cited:</strong>{' '}
          click <strong>Regenerate</strong> to have the compliance
          assistant write a hazard record — citing the control and naming
          a mitigation — into the compliance section, then re-check
          automatically.{' '}
          <strong className="text-amber-200">
            Out of scope or a false positive:
          </strong>{' '}
          click <strong>Waive</strong> on the finding and record why — the
          reason is saved to the audit trail and the pre-export gate stops
          blocking on it. Either way, you don't edit any files by hand.
        </p>
      </div>
    </div>
  )
}
